import React, { useRef } from 'react';
import { Base } from './ALinkStandalone.styles';
import { type ALinkStandaloneProps, type ALinkStandaloneAnchorProps, type ALinkStandaloneButtonProps, ALinkStandaloneSize } from './ALinkStandalone.types';
import { DEFAULT_OPEN_IN_NEW_TAB_TEXT } from '../../utils/i18nConstants';
import clsx from 'clsx';
import { AIcon, AIconSize } from '../AIcon/index';
import { useTracking } from '../../hooks';

const COMPONENT_NAME = 'a-web-ui-kit-ALinkStandalone';
const TEST_ID = 'web-ui-kit-ALinkStandalone';

const ALinkStandalone = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, ALinkStandaloneProps>(
  (
    {
      text,
      children,
      size = ALinkStandaloneSize.Medium,
      className = '',
      trackingProperties,
      custom_click_track_value,
      preTrackVal,
      ...rest
    },
    ref
  ) => {
    const resolvedHref = rest.ctaLink ?? rest.href;
    const renderAsLink = resolvedHref !== undefined;

    const externalLink = renderAsLink ? (rest as ALinkStandaloneAnchorProps).externalLink ?? false : false;
    const openInANewTab = renderAsLink ? (rest as ALinkStandaloneAnchorProps).openInANewTab ?? false : false;
    const openInANewTabText = renderAsLink
      ? (rest as ALinkStandaloneAnchorProps).openInANewTabText ?? DEFAULT_OPEN_IN_NEW_TAB_TEXT
      : DEFAULT_OPEN_IN_NEW_TAB_TEXT;

    const isExternal =
      renderAsLink &&
      (openInANewTab === true ||
        openInANewTab === 'true' ||
        externalLink === true ||
        externalLink === 'true' ||
        (rest as ALinkStandaloneAnchorProps).target === '_blank');

    const defaultRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null);
    const elementRef = (ref ?? defaultRef) as React.RefObject<HTMLAnchorElement | HTMLButtonElement>;

    const extendedClassName = clsx(className, 'm-link', {
      md: size === ALinkStandaloneSize.Medium,
      sm: size === ALinkStandaloneSize.Small,
      xs: size === ALinkStandaloneSize.ExtraSmall,
    });

    useTracking({
      ref: elementRef,
      trackingProperties,
      isExternal,
      custom_click_track_value,
      preTrackVal,
      text,
    });

    const ExternalIndicator = (
      <AIcon
        size={AIconSize.S}
        icon="icon-external-arrow"
        className="icon-container d-inline-block ml-1"
        aria-label={openInANewTabText}
      />
    );

    if (renderAsLink) {
      const { ctaLink: _c, href: _h, externalLink: _e, openInANewTab: _o, openInANewTabText: _t, ...anchorRest } =
        rest as ALinkStandaloneAnchorProps;
      const target = anchorRest.target;
      const rel = anchorRest.rel;
      const _target = isExternal ? '_blank' : target;
      const resolvedRel = rel ?? (_target === '_blank' ? 'noopener noreferrer' : undefined);
      const showAsExternal = _target === '_blank';

      return (
        <Base
          as="a"
          href={resolvedHref}
          ref={elementRef as React.Ref<HTMLAnchorElement>}
          data-component-name={COMPONENT_NAME}
          data-testid={TEST_ID}
          {...anchorRest}
          className={clsx(extendedClassName, { ext: showAsExternal })}
          target={_target}
          rel={resolvedRel}
        >
          {children ?? text}
          {showAsExternal && <> {ExternalIndicator}</>}
        </Base>
      );
    }

    const { ctaLink: _c, href: _h, ...buttonRest } = rest as ALinkStandaloneButtonProps;
    const type = buttonRest.type ?? 'button';

    return (
      <Base
        as="button"
        type={type}
        ref={elementRef as React.Ref<HTMLButtonElement>}
        data-component-name={COMPONENT_NAME}
        data-testid={TEST_ID}
        {...buttonRest}
        className={extendedClassName}
      >
        {children ?? text}
      </Base>
    );
  }
);

ALinkStandalone.displayName = 'ALinkStandalone';

export { ALinkStandalone, ALinkStandaloneSize, TEST_ID as A_LINK_STANDALONE_TEST_ID };
